/**
 * Zod schemas and helpers for conversational onboarding flow
 * Purpose: Type-safe parsing and validation of LLM-generated onboarding data
 * For ONBOARDING_SYSTEM_PROMPT, see @/lib/onboarding/prompt.ts
 */

import { z } from "zod";
import { STEP_IDS, type StepId } from "@/constants/steps";

/**
 * Reasoning effort for the onboarding chat (Vercel AI SDK streamObject).
 * The model itself is FLAGSHIP_MODEL_ID, resolved in the onboarding-chat API
 * route — not here, because this file is imported by client components and
 * must not pull in @/lib/ai/modelConfig (which reads server env).
 */
export const ONBOARDING_REASONING_EFFORT = "none" as const;

/**
 * Truncate instead of reject. OpenAI structured outputs do NOT enforce
 * maxLength, so the model CAN overshoot any cap — a hard `.max()` turns a
 * complete, usable response into AI_NoObjectGeneratedError and fails the
 * founder's entire onboarding turn (Sentry LEVAI-7665559835). The cap still
 * bounds what we store; `.describe()` text keeps steering the model.
 */
const truncated = (max: number) => z.string().transform((val) => val.slice(0, max));

/** Array counterpart: truncate each item AND the list length. */
const truncatedList = (itemMax: number, listMax: number) =>
  z.array(truncated(itemMax)).transform((arr) => arr.slice(0, listMax));

/**
 * Boolean that tolerates explicit null. `.default()` fills undefined (absent)
 * but rejects explicit null — and the model treats null as "don't know yet"
 * schema-wide, so it routinely emits `"deckParsed": null`. Without this,
 * Zod rejects → AI_TypeValidationError → AI_NoObjectGeneratedError → the
 * founder's entire onboarding turn hard-fails.
 */
const lenientBoolean = (def: boolean) =>
  z.boolean().nullish().transform((v) => v ?? def).catch(def);

/** String: truncates, tolerates null/undefined AND wrong types (number, object, array). */
const lenientString = (max: number) => truncated(max).nullish().catch(null);

/** List: item + length caps, tolerates null/undefined AND wrong types. */
const lenientList = (itemMax: number, listMax: number) =>
  truncatedList(itemMax, listMax).nullish().catch(null);

// Inner object kept addressable: ZodCatch has no .partial(), and the
// partial-finalize schema below needs the bare object shape.
const OnboardingIdentityObject = z.object({
  // LLM may output invalid URLs/emails - accept any string, null for invalid
  linkedinUrl: lenientString(500)
    .describe(
      "The founder's LinkedIn URL, resolved from a full URL, partial URL (linkedin.com/handle), or bare handle they typed. Always store the full https://www.linkedin.com/in/<handle> form. Set it the moment they provide it — never wait for confirmation.",
    ),
  name: lenientString(200),
  email: lenientString(500),
  title: lenientString(200),
  background: lenientString(2000),
  /// Founder's PERSONAL site (not the company site) — snapshotted for founder
  /// memory enrichment, never persisted to the User row.
  personalWebsite: lenientString(500),
});
export const OnboardingIdentitySchema = OnboardingIdentityObject.catch({
  linkedinUrl: null, name: null, email: null,
  title: null, background: null, personalWebsite: null,
});


// Valid journey stages - LLM may output variations, so we coerce to "unknown" if invalid
export const VALID_STAGES = [
  "idea",
  "validation",
  "early_traction",
  "growth",
  "pivoting",
  "unknown",
] as const;

// Valid raising intent values (Phase 3c R2)
export const VALID_RAISING_INTENTS = [
  "raising",
  "planning",
  "not_raising",
  "unknown",
] as const;

// Valid launch status values (Phase 3c R2)
export const VALID_LAUNCH_STATUSES = [
  "launched",
  "test_balloon",
  "not_launched",
  "unknown",
] as const;
const OnboardingJourneyObject = z.object({
  // LLM often outputs creative stage names - coerce to "unknown" if not in our enum
  stage: z
    .string()
    .transform((val) =>
      VALID_STAGES.includes(val as (typeof VALID_STAGES)[number]) ? val : "unknown",
    )
    .pipe(z.enum(VALID_STAGES))
    .catch("unknown"),
  narrative: lenientString(2000),
  milestones: lenientList(500, 20),
});
export const OnboardingJourneySchema = OnboardingJourneyObject.catch({
  stage: "unknown" as const,
  narrative: null,
  milestones: null,
});

const OnboardingBigIdeaObject = z.object({
  oneLiner: truncated(60).nullish().catch(null)
    .describe(
      "A concise project title (max 60 chars, 3-8 words). This should name the actual startup concept, not just a broad category like 'AI'. Include the idea/product name if the user provides one. Example: 'Freelancer Back-Office Copilot'.",
    ),
  problem: lenientString(2000)
    .describe("The concrete pain point or job-to-be-done the startup solves. Required before status can be 'complete'."),
  solution: lenientString(2000)
    .describe("The product or workflow solution being proposed. Required before status can be 'complete'."),
  targetAudience: lenientString(1000)
    .describe("The specific initial customer or user segment. Required before status can be 'complete'."),
  rawIdea: lenientString(5000)
    .describe("A faithful paraphrase of the founder's idea pitch in their own framing. Condense to under 5000 characters while preserving key details, voice, and specifics. Not a summary — capture the substance, skip only redundancy."),
  refinedIdea: lenientString(5000)
    .describe(
      "A polished narrative synthesizing the user's idea. Combine the problem, solution, and target audience into flowing prose. Update this continuously as you learn more during the conversation. This is used to generate the idea statement and should be detailed enough to be used as a standalone idea statement. Required before status can be 'complete'.",
    ),
  bigIdeaContent: lenientString(10000)
    .describe("Progressive markdown document in Big Idea shape (## Elevator pitch + ## The detail). Rewritten as understanding deepens. Ground every number in what the founder or their materials stated. Re-emit only when changed."),
});
export const OnboardingBigIdeaSchema = OnboardingBigIdeaObject.catch({
  oneLiner: null, problem: null, solution: null,
  targetAudience: null, rawIdea: null, refinedIdea: null,
  bigIdeaContent: null,
});

// ---------------------------------------------------------------------------
// Step-mapped capture groups (LEV-1395 §1)
// ---------------------------------------------------------------------------

const CompetitorEntrySchema = z.object({
  name: lenientString(120),
  note: lenientString(300),
}).nullish().catch(null);

const MarketClaimSchema = z.object({
  claim: lenientString(300),
  evidence: lenientString(300),
  source: lenientString(200),
}).nullish().catch(null);

const RoadmapMilestoneSchema = z.object({
  milestone: lenientString(300),
  timeframe: lenientString(100),
}).nullish().catch(null);

const TeamMemberSchema = z.object({
  name: lenientString(120),
  role: lenientString(200),
  background: lenientString(500),
}).nullish().catch(null);

const CustomerProblemGroupSchema = z.object({
  icp: lenientString(500).describe("Ideal customer profile — who suffers most."),
  pains: lenientString(1000).describe("Pain points or jobs-to-be-done customers face today."),
  alternatives: lenientString(500).describe("What they do today instead."),
}).nullish().catch(null);

const CompetitiveGroupSchema = z.object({
  competitors: z.array(CompetitorEntrySchema).transform((arr) => arr.slice(0, 20)).nullish().catch(null).describe("Named competitors with brief notes (max 20)."),
  differentiation: lenientString(500).describe("How the founder differentiates."),
}).nullish().catch(null);

const MarketGroupSchema = z.object({
  claims: z.array(MarketClaimSchema).transform((arr) => arr.slice(0, 10)).nullish().catch(null).describe("Market claims with evidence and source (max 10)."),
  vertical: lenientString(200).describe("Target vertical or industry."),
}).nullish().catch(null);

const BrandGroupSchema = z.object({
  name: lenientString(120).describe("Brand / product name."),
  status: lenientString(200).describe("Branding status — e.g. 'have a name', 'need a name', 'have full brand kit'."),
  values: lenientString(500).describe("Brand values or personality."),
  tagline: lenientString(280).describe("Brand tagline or value prop."),
}).nullish().catch(null);

const GtmGroupSchema = z.object({
  channels: lenientString(500).describe("Go-to-market channels."),
  pricingModel: lenientString(200).describe("Pricing model — freemium, usage-based, flat-rate, etc."),
  pricePoint: lenientString(200).describe("Specific price point or range."),
}).nullish().catch(null);

const ProductBuildGroupSchema = z.object({
  description: lenientString(1000).describe("Product description."),
  techStack: lenientString(500).describe("Technology stack."),
  roadmap: z.array(RoadmapMilestoneSchema).transform((arr) => arr.slice(0, 10)).nullish().catch(null).describe("Product roadmap milestones (max 10)."),
}).nullish().catch(null);

const TeamGroupSchema = z.object({
  members: z.array(TeamMemberSchema).transform((arr) => arr.slice(0, 10)).nullish().catch(null).describe("Team members with roles and backgrounds (max 10)."),
  hiringPriorities: lenientString(500).describe("Key hiring priorities."),
}).nullish().catch(null);

const OnboardingStepsSchema = z.object({
  customerProblem: CustomerProblemGroupSchema,
  competitive: CompetitiveGroupSchema,
  market: MarketGroupSchema,
  brand: BrandGroupSchema,
  gtm: GtmGroupSchema,
  productBuild: ProductBuildGroupSchema,
  team: TeamGroupSchema,
}).nullish().catch(null);

const FundraiseGroupSchema = z.object({
  amount: lenientString(100).describe("Raise amount."),
  instrument: lenientString(100).describe("Instrument — SAFE, priced round, convertible note, etc."),
  valuation: lenientString(100).describe("Valuation or cap."),
  status: lenientString(200).describe("Fundraise status."),
  history: lenientString(500).describe("Prior funding history."),
}).nullish().catch(null);

const TractionGroupSchema = z.object({
  revenue: lenientString(200).describe("Revenue figures — ONLY what the founder explicitly states. Never infer or estimate."),
  users: lenientString(200).describe("User or customer counts — ONLY explicit statements."),
  growthRate: lenientString(200).describe("Growth rate — ONLY explicit statements."),
  customers: lenientString(500).describe("Customer descriptions or notable names."),
}).nullish().catch(null);

export type OnboardingSteps = z.infer<typeof OnboardingStepsSchema>;

/**
 * 1-1 map from canvas step → capture group key. The IDEA step maps to
 * "bigIdea" (top-level, not steps.*); the other content steps map to their
 * corresponding steps.* group key.
 *
 * FUNDRAISING maps to null: it is an in-app research surface whose
 * onboarding-relevant inputs come from the fundraise/traction company-state
 * groups already captured by the prompt.
 *
 * `null` is always an EXPLICIT decision that a step has no onboarding
 * capture, never an omission — adding a canvas step without deciding its
 * capture here fails the build via the completeness test.
 */
export const ONBOARDING_STEP_CAPTURE: Record<StepId, "bigIdea" | "fundraise" | keyof NonNullable<OnboardingSteps> | null> = {
  [STEP_IDS.IDEA]: "bigIdea",
  [STEP_IDS.CUSTOMER_PROBLEM]: "customerProblem",
  [STEP_IDS.COMPETITIVE]: "competitive",
  [STEP_IDS.TAM]: "market",
  [STEP_IDS.BRAND]: "brand",
  [STEP_IDS.GTM]: "gtm",
  [STEP_IDS.PRODUCT_BUILD]: "productBuild",
  [STEP_IDS.TEAM]: "team",
  [STEP_IDS.FUNDRAISING]: null,
};

/**
 * Presence-preserving: NO `.default("venture")`. Under delta emission the model
 * omits unchanged fields, and a parse-time default would rewrite that absence
 * as "venture" — the enrich-only merge would then overwrite a founder's earlier
 * "bootstrap" choice every turn (Copilot P1 on PR #688). Absence stays
 * null/undefined into the merge; mergeWithDefaults still fills "venture" for
 * the normalized read path.
 */
export const OnboardingRubricTypeSchema = z
  .enum(["venture", "bootstrap"])
  .describe("venture or bootstrap. Default to venture. Ask about this once the idea is taking shape—it's secondary to understanding the idea itself. Omit unless the founder's answer or materials establish it.")
  .nullish()
  .catch(null);

const VALID_SALES_MOTIONS = ["self_serve", "sales_led", "unclear"] as const;
export type OnboardingSalesMotion = (typeof VALID_SALES_MOTIONS)[number];
export const OnboardingSiteSignalsSchema = z
  .object({
    url: lenientString(1000).describe("The URL the founder pasted (canonical form)."),
    inferredName: lenientString(120).describe("Company / product name as it appears on the site."),
    tagline: lenientString(280).describe("Hero tagline or one-line value prop."),
    icpHint: lenientString(280).describe("Who the site says it's for (ICP)."),
    hasPricing: z.boolean().nullish().catch(null).describe("True if the site shows public pricing."),
    salesMotion: z
      .string()
      .transform((v) =>
        VALID_SALES_MOTIONS.includes(v as (typeof VALID_SALES_MOTIONS)[number]) ? v : "unclear",
      )
      .pipe(z.enum(VALID_SALES_MOTIONS))
      .catch("unclear")
      .nullable()
      .optional()
      .describe("self_serve, sales_led, or unclear based on calls-to-action and pricing."),
    customerLogos: lenientList(80, 20).describe("Customer/partner names visibly featured."),
    foundersInferred: lenientList(120, 10).describe("Founder names visible on the site (team / about page)."),
  })
  .nullable()
  .optional()
  .catch(null);

/**
 * Structured-output schema for the deterministic materials→capture extraction
 * (2026-08-20): after a MaterialsCritique is persisted, an extraction pass maps
 * the parsed materials into the capture groups so deck-stated facts reach
 * memory synthesis and generation context even when the chat never touches
 * them. Mirrors OnboardingResultSchema's capture groups minus the
 * conversation-owned fields: no `status` (extraction is not a conversation
 * authority), no `bigIdea.bigIdeaContent`/`rawIdea` (progressive markdown and
 * the founder's own-words pitch are chat artifacts), no `launchStatus` (never
 * inferred from materials — the founder must confirm), no `journey.stage`
 * (stage compass owns assessment). Output maps 1:1 onto
 * PartialOnboardingResult for the gap-fill merge in
 * `runMaterialsCaptureForSession` — existing capture always wins.
 *
 * `siteSignals` (LEV-1427) reuses OnboardingSiteSignalsSchema so a website
 * critique fills the same shape the chat model does; the orchestrator keeps
 * it only for `sourceType: "site"` critiques. Declared after
 * OnboardingSiteSignalsSchema because `const` bindings are not hoisted.
 */
export const MaterialsCaptureExtractionSchema = z.object({
  identity: z.object({
    name: lenientString(200).describe("Founder's name ONLY when the materials state it (cover or team slide)."),
    title: lenientString(200).describe("Founder's role/title exactly as stated."),
    background: lenientString(2000).describe("Founder background as stated — prior roles, companies, credentials. Prior-company achievements belong HERE, never in traction."),
    email: lenientString(500).describe("Founder's contact email ONLY if literally printed in the materials."),
    personalWebsite: lenientString(500).describe("Founder's PERSONAL site if stated — never the company site."),
  }).nullish().catch(null),
  journey: z.object({
    narrative: lenientString(2000).describe("The company's journey/history as the materials tell it."),
    milestones: lenientList(500, 20).describe("Concrete milestones the materials state (pilots, LOIs, launches, waitlist counts). Label future targets as goals — e.g. 'Goal: $1M ARR in 12 months'."),
  }).nullish().catch(null),
  bigIdea: z.object({
    oneLiner: truncated(60).nullish().catch(null).describe("Concise project title naming the actual concept (max 60 chars)."),
    problem: lenientString(2000).describe("The pain point the materials describe."),
    solution: lenientString(2000).describe("The product/workflow solution the materials describe."),
    targetAudience: lenientString(1000).describe("The initial customer segment the materials name."),
    refinedIdea: lenientString(5000).describe("A polished narrative synthesizing the materials' problem, solution, and target audience into flowing prose."),
  }).nullish().catch(null),
  raisingIntent: z.enum(["raising", "planning", "unknown"]).nullish().catch(null)
    .describe("'raising' or 'planning' ONLY when the materials contain an explicit ask/raise statement; otherwise 'unknown'. NEVER 'not_raising' from absence."),
  steps: OnboardingStepsSchema,
  fundraise: FundraiseGroupSchema,
  traction: TractionGroupSchema,
  siteSignals: OnboardingSiteSignalsSchema.describe("ONLY when the materials are a website: what the site literally states about itself. Null for a pitch deck."),
});
export type MaterialsCaptureExtraction = z.infer<typeof MaterialsCaptureExtractionSchema>;

// Valid status values - default to "partial" during streaming (only "complete" triggers ready state)
const VALID_STATUSES = ["complete", "skipped", "partial", "limit_exceeded"] as const;
export const OnboardingResultSchema = z.object({
  status: z
    .string()
    .transform((val) =>
      VALID_STATUSES.includes(val as (typeof VALID_STATUSES)[number]) ? val : "partial",
    )
    .pipe(z.enum(VALID_STATUSES))
    .describe(
      "Set to 'complete' only when bigIdea.problem, bigIdea.solution, bigIdea.targetAudience, and bigIdea.refinedIdea are all populated, AND your message field contains a wrap-up statement (not a question). Do NOT complete if any of those fields are missing. Also do NOT complete until identity is resolved: identity.linkedinUrl, identity.personalWebsite, or identity.background is set, or the founder explicitly declined.",
    )
    .catch("partial"),
  identity: OnboardingIdentitySchema,
  journey: OnboardingJourneySchema,
  bigIdea: OnboardingBigIdeaSchema,
  rubricType: OnboardingRubricTypeSchema,
  raisingIntent: z
    .string()
    .transform((val) =>
      VALID_RAISING_INTENTS.includes(val as (typeof VALID_RAISING_INTENTS)[number]) ? val : "unknown",
    )
    .pipe(z.enum(VALID_RAISING_INTENTS))
    .catch("unknown")
    .describe("Raising intent: raising, planning, not_raising, unknown."),
  launchStatus: z
    .string()
    .transform((val) =>
      VALID_LAUNCH_STATUSES.includes(val as (typeof VALID_LAUNCH_STATUSES)[number]) ? val : "unknown",
    )
    .pipe(z.enum(VALID_LAUNCH_STATUSES))
    .catch("unknown")
    .describe("Launch status. NEVER infer launched from a URL. Only set 'launched' when founder explicitly confirms."),
  brandingStatus: lenientString(200)
    .describe("What is the branding/naming status? e.g. 'have a name', 'need a name', 'have full brand kit'."),
  materials: z.object({
    deckParsed: lenientBoolean(false),
    siteConfirmed: lenientBoolean(false),
    /** True once the model has delivered the website read (clears [MATERIALS_READ_DUE]); optional so rows written before it existed still parse. */
    siteRead: z.boolean().optional().catch(undefined),
    critiqueId: z.string().nullish().catch(null),
  }).nullable().optional().catch(null)
    .describe("Tracks what materials have been processed."),
  siteSignals: OnboardingSiteSignalsSchema,
  notes: lenientList(1000, 20),
  steps: OnboardingStepsSchema.transform((v) => v ?? null),
  fundraise: FundraiseGroupSchema.transform((v) => v ?? null),
  traction: TractionGroupSchema.transform((v) => v ?? null),
});

export type OnboardingResult = z.infer<typeof OnboardingResultSchema>;
export type OnboardingIdentity = z.infer<typeof OnboardingIdentitySchema>;
export type OnboardingJourney = z.infer<typeof OnboardingJourneySchema>;
export type OnboardingBigIdea = z.infer<typeof OnboardingBigIdeaSchema>;
export type OnboardingSiteSignals = z.infer<typeof OnboardingSiteSignalsSchema>;
export type OnboardingRaisingIntent = (typeof VALID_RAISING_INTENTS)[number];
export type OnboardingLaunchStatus = (typeof VALID_LAUNCH_STATUSES)[number];
export type OnboardingMaterials = z.infer<typeof OnboardingResultSchema>["materials"];

/**
 * Parse schema for client finalize/claim backup payloads. Identical shape to
 * OnboardingResultSchema but omits defaults on fields whose default is a valid
 * domain value (rubricType) so absent stays absent into enrich-only merge.
 *
 * NEVER HARD-FAIL: every leaf MUST use a lenient helper (lenientString,
 * lenientBoolean, lenientList) or terminate in .catch(). A bare
 * `.optional()` rejects explicit null — the client accumulates result
 * fields across streaming turns and the model routinely emits null for
 * "not yet known". A Zod rejection here returns INVALID_RESULT to the
 * client, which surfaces as "We couldn't save your conversation" and
 * blocks the founder's Skip/Build handoff. The PATCH route now captures
 * both rejection branches to Sentry, but the schema must still never
 * reject — a capture is observability, not a fix.
 */
export const OnboardingPartialFinalizeResultSchema = z.object({
  status: OnboardingResultSchema.shape.status.optional().catch(undefined),
  identity: OnboardingIdentityObject.partial().nullish().catch(null),
  journey: OnboardingJourneyObject.partial().nullish().catch(null),
  bigIdea: OnboardingBigIdeaObject.partial().nullish().catch(null),
  // Capture groups (LEV-1395): zod strips unknown keys, so omitting these
  // here silently discarded every captured group on the finalize/claim-backup
  // paths (Copilot P1 on PR #688). Reuse the streaming schemas so the shapes
  // can never drift apart.
  steps: OnboardingStepsSchema,
  fundraise: FundraiseGroupSchema,
  traction: TractionGroupSchema,
  // Reuse the streaming schema's leaves where the shape is identical so the
  // two can never drift apart (rubricType stays presence-preserving; materials
  // booleans stay lenient; notes stays lenient). Sentry LEV-ET.
  rubricType: OnboardingRubricTypeSchema,
  raisingIntent: OnboardingResultSchema.shape.raisingIntent.optional().catch(undefined),
  launchStatus: OnboardingResultSchema.shape.launchStatus.optional().catch(undefined),
  brandingStatus: OnboardingResultSchema.shape.brandingStatus.optional().catch(undefined),
  materials: OnboardingResultSchema.shape.materials,
  siteSignals: OnboardingSiteSignalsSchema.optional().catch(null),
  notes: OnboardingResultSchema.shape.notes,
});

export type OnboardingPartialFinalizeResult = z.infer<
  typeof OnboardingPartialFinalizeResultSchema
>;

/**
 * Schema for every onboarding chat API response.
 * - message: conversational text shown in chat bubble
 * - result: structured data, present only when concluding
 */
const OnboardingResponseObject = z.object({
  message: z
    .string()
    .catch("")
    .describe(
      "Your conversational response to the user. This is displayed in the chat. When concluding, this MUST contain a wrap-up message like 'I've got all I need, let's build!'",
    ),
  result: OnboardingResultSchema.optional()
    .catch(undefined)
    .describe(
      "Include in every response. Prioritize bigIdea fields. Before using status='complete', ensure bigIdea.problem, bigIdea.solution, bigIdea.targetAudience, and bigIdea.refinedIdea are all populated. Keep identity fields updated every turn as you learn them — a bare LinkedIn handle resolves to a full https://www.linkedin.com/in/<handle> URL immediately.",
    ),
});

/**
 * Total at the ROOT: field-level catches only run after z.object accepts the
 * root value, so without this a root-level primitive/array (safeParse(null),
 * safeParse(42)) would fail — breaking the advertised "every well-formed JSON
 * value parses" invariant (Copilot P1 on PR #688). Catastrophic root garbage
 * degrades to an empty turn instead of a thrown turn.
 */
export const OnboardingResponseSchema = OnboardingResponseObject.catch({
  message: "",
  result: undefined,
});

export type OnboardingResponse = z.infer<typeof OnboardingResponseSchema>;

/**
 * Creates a default/empty onboarding result
 */
export function createDefaultOnboardingResult(): OnboardingResult {
  return {
    status: "partial",
    identity: {
      linkedinUrl: null,
      name: null,
      email: null,
      title: null,
      background: null,
      personalWebsite: null,
    },
    journey: {
      stage: "unknown",
      narrative: null,
      milestones: null,
    },
    bigIdea: {
      oneLiner: null,
      problem: null,
      solution: null,
      targetAudience: null,
      // uploadedFiles: null,
      rawIdea: null,
      refinedIdea: null,
      bigIdeaContent: null,
    },
    rubricType: "venture",
    raisingIntent: "unknown",
    launchStatus: "unknown",
    brandingStatus: null,
    materials: null,
    siteSignals: null,
    notes: null,
    steps: null,
    fundraise: null,
    traction: null,
  };
}

/**
 * Partial onboarding result (e.g. from stream or state). Used for merging with defaults.
 */
/** Partial step groups for merge/delta input. */
type PartialSteps = {
  customerProblem?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["customerProblem"]>> | null;
  competitive?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["competitive"]>> | null;
  market?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["market"]>> | null;
  brand?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["brand"]>> | null;
  gtm?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["gtm"]>> | null;
  productBuild?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["productBuild"]>> | null;
  team?: Partial<NonNullable<NonNullable<OnboardingResult["steps"]>["team"]>> | null;
} | null;

export type PartialOnboardingResult = {
  status?: OnboardingResult["status"];
  identity?: Partial<OnboardingResult["identity"]>;
  journey?: Partial<OnboardingResult["journey"]>;
  bigIdea?: Partial<OnboardingResult["bigIdea"]>;
  rubricType?: OnboardingResult["rubricType"];
  raisingIntent?: OnboardingResult["raisingIntent"];
  launchStatus?: OnboardingResult["launchStatus"];
  brandingStatus?: OnboardingResult["brandingStatus"];
  materials?: OnboardingResult["materials"];
  siteSignals?: OnboardingResult["siteSignals"];
  notes?: OnboardingResult["notes"];
  steps?: PartialSteps;
  fundraise?: Partial<NonNullable<OnboardingResult["fundraise"]>> | null;
  traction?: Partial<NonNullable<OnboardingResult["traction"]>> | null;
};

/**
 * Merges an existing (possibly partial) result with default values.
 * Only overwrites defaults where existing has a defined value.
 * Used for both Complete (full merge) and Skip (merge then set status + rawIdea).
 */
export function mergeWithDefaults(
  existing: PartialOnboardingResult | OnboardingResult | null | undefined,
): OnboardingResult {
  const def = createDefaultOnboardingResult();
  if (!existing) return def;
  return {
    status: existing.status ?? def.status,
    identity: mergeIdentity(def.identity, existing.identity),
    journey: mergeJourney(def.journey, existing.journey),
    bigIdea: mergeBigIdea(def.bigIdea, existing.bigIdea),
    rubricType: existing.rubricType ?? def.rubricType,
    raisingIntent: existing.raisingIntent ?? def.raisingIntent,
    launchStatus: existing.launchStatus ?? def.launchStatus,
    brandingStatus: existing.brandingStatus !== undefined ? existing.brandingStatus : def.brandingStatus,
    materials: existing.materials !== undefined ? existing.materials : def.materials,
    siteSignals: existing.siteSignals !== undefined ? existing.siteSignals : def.siteSignals,
    notes: existing.notes !== undefined ? existing.notes : def.notes,
    steps: existing.steps !== undefined ? existing.steps as OnboardingResult["steps"] : def.steps,
    fundraise: existing.fundraise !== undefined ? existing.fundraise as OnboardingResult["fundraise"] : def.fundraise,
    traction: existing.traction !== undefined ? existing.traction as OnboardingResult["traction"] : def.traction,
  };
}

/**
 * True when the value has at least one non-empty string leaf, boolean, or
 * non-empty array (recursing into nested non-array objects). Shared emptiness
 * gate for memory synthesis and the materials-capture extraction —
 * consolidated from two drifting private copies (/simplify, 2026-08-20); the
 * boolean branch is deliberate: a boolean leaf IS content.
 */
export function hasNonEmptyLeaf(obj: unknown): boolean {
  if (!obj || typeof obj !== "object") return false;
  for (const val of Object.values(obj)) {
    if (typeof val === "string" && val.trim()) return true;
    if (typeof val === "boolean") return true;
    if (Array.isArray(val) && val.length > 0) return true;
    if (val && typeof val === "object" && !Array.isArray(val)) {
      if (hasNonEmptyLeaf(val)) return true;
    }
  }
  return false;
}

function enrichPick<T>(a: T | null | undefined, b: T | null | undefined): T | null {
  if (a !== undefined && a !== null) {
    if (typeof a === "string" && a.trim() === "") {
      return b !== undefined && b !== null ? b : null;
    }
    return a;
  }
  if (b !== undefined && b !== null) return b;
  return null;
}

function enrichPickArray<T>(a: T[] | null | undefined, b: T[] | null | undefined): T[] | null {
  if (a !== undefined && a !== null) {
    if (Array.isArray(a) && a.length === 0) {
      return b !== undefined && b !== null ? b : null;
    }
    return a;
  }
  if (b !== undefined && b !== null) return b;
  return null;
}

function mergeSiteSignals(
  base: OnboardingResult["siteSignals"],
  next: PartialOnboardingResult["siteSignals"],
): OnboardingResult["siteSignals"] {
  if (next === undefined || next === null) return base;
  if (!base) return next as OnboardingResult["siteSignals"];
  return {
    url: enrichPick(next.url, base.url),
    inferredName: enrichPick(next.inferredName, base.inferredName),
    tagline: enrichPick(next.tagline, base.tagline),
    icpHint: enrichPick(next.icpHint, base.icpHint),
    hasPricing: next.hasPricing !== undefined && next.hasPricing !== null ? next.hasPricing : base.hasPricing,
    salesMotion: enrichPick(next.salesMotion, base.salesMotion),
    customerLogos: enrichPickArray(next.customerLogos, base.customerLogos),
    foundersInferred: enrichPickArray(next.foundersInferred, base.foundersInferred),
  };
}

/** A single onboarding chat message — shared by the chat route, turn persistence, and the finalize PATCH. */
export type OnboardingChatMessage = {
  role: "user" | "assistant";
  content: string;
};

/**
 * Merge onboarding chat transcripts monotonically. Empty never clobbers; when one
 * exact {role, content} sequence is a prefix of the other, keep the longer; on
 * divergence prefer more user turns, then more total messages, then the server
 * transcript on a tie. Never concatenates divergent arrays.
 */
export function mergeOnboardingMessages(
  base: OnboardingChatMessage[],
  next: OnboardingChatMessage[],
): OnboardingChatMessage[] {
  if (!next.length) return base;
  if (!base.length) return next;

  const isPrefix = (prefix: OnboardingChatMessage[], candidate: OnboardingChatMessage[]) => {
    if (prefix.length > candidate.length) return false;
    for (let i = 0; i < prefix.length; i++) {
      if (prefix[i].role !== candidate[i].role || prefix[i].content !== candidate[i].content) {
        return false;
      }
    }
    return true;
  };

  if (isPrefix(base, next)) return next;
  if (isPrefix(next, base)) return base;

  const userBase = base.filter((m) => m.role === "user").length;
  const userNext = next.filter((m) => m.role === "user").length;
  if (userNext > userBase) return next;
  if (userBase > userNext) return base;
  if (next.length > base.length) return next;
  if (base.length > next.length) return base;
  return base;
}

/** Structural check: does this value look like an OnboardingResult (vs an envelope or junk)? */
export function looksLikeOnboardingResult(value: unknown): value is PartialOnboardingResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return "bigIdea" in v || "identity" in v || "journey" in v || "status" in v
    || "steps" in v || "fundraise" in v || "traction" in v
    || "materials" in v || "siteSignals" in v || "rubricType" in v;
}

/**
 * Normalize a persisted onboarding result of unknown provenance into a full
 * OnboardingResult. Accepts:
 * - a bare (possibly partial) OnboardingResult — merged with defaults
 * - the streamed `{ message, result }` response envelope — rows written by the
 *   chat route before 2026-07-30 persisted the whole envelope, and those rows
 *   live on in OnboardingSession.result and uiState.onboardingContext.result
 * - null/undefined/junk — returns defaults
 *
 * Every reader of a persisted onboarding result (claim consumer, agent
 * factory, auto-rebuild, stage compass) MUST go through this instead of
 * casting: the envelope leak named projects "Untitled idea" and crashed agent
 * creation on `result.identity.name`.
 */
export function normalizeOnboardingResult(raw: unknown): OnboardingResult {
  let result: OnboardingResult;
  if (looksLikeOnboardingResult(raw)) {
    result = mergeWithDefaults(raw);
  } else if (raw && typeof raw === "object") {
    const nested = (raw as Record<string, unknown>).result;
    if (looksLikeOnboardingResult(nested)) {
      result = mergeWithDefaults(nested);
    } else {
      return createDefaultOnboardingResult();
    }
  } else {
    return createDefaultOnboardingResult();
  }
  // Read-shim: map legacy brandingStatus → steps.brand.status when the
  // brand group is empty (the prompt stops writing brandingStatus in task 3;
  // old rows carry it). Do NOT overwrite an existing brand.status.
  if (result.brandingStatus && !result.steps?.brand?.status) {
    result = {
      ...result,
      steps: {
        ...(result.steps ?? {
          customerProblem: null, competitive: null, market: null,
          brand: null, gtm: null, productBuild: null, team: null,
        }),
        brand: {
          ...(result.steps?.brand ?? { name: null, values: null, tagline: null }),
          status: result.brandingStatus,
        },
      },
    };
  }
  return result;
}

function mergeIdentity(
  def: OnboardingResult["identity"],
  existing?: Partial<OnboardingResult["identity"]> | Record<string, unknown> | null,
): OnboardingResult["identity"] {
  if (!existing) return def;
  const merged: Record<string, unknown> = {
    linkedinUrl: existing.linkedinUrl !== undefined ? existing.linkedinUrl : def.linkedinUrl,
    name: existing.name !== undefined ? existing.name : def.name,
    email: existing.email !== undefined ? existing.email : def.email,
    title: existing.title !== undefined ? existing.title : def.title,
    background: existing.background !== undefined ? existing.background : def.background,
    personalWebsite:
      existing.personalWebsite !== undefined ? existing.personalWebsite : def.personalWebsite,
  };
  for (const [key, value] of Object.entries(existing)) {
    if (key in merged) continue;
    if (value !== undefined) merged[key] = value;
  }
  return merged as OnboardingResult["identity"];
}

function mergeJourney(
  def: OnboardingResult["journey"],
  existing?: Partial<OnboardingResult["journey"]> | null,
): OnboardingResult["journey"] {
  if (!existing) return def;
  return {
    stage: existing.stage !== undefined ? existing.stage : def.stage,
    narrative: existing.narrative !== undefined ? existing.narrative : def.narrative,
    milestones: existing.milestones !== undefined ? existing.milestones : def.milestones,
  };
}

function mergeBigIdea(
  def: OnboardingResult["bigIdea"],
  existing?: Partial<OnboardingResult["bigIdea"]> | null,
): OnboardingResult["bigIdea"] {
  if (!existing) return def;
  return {
    oneLiner: existing.oneLiner !== undefined ? existing.oneLiner : def.oneLiner,
    problem: existing.problem !== undefined ? existing.problem : def.problem,
    solution: existing.solution !== undefined ? existing.solution : def.solution,
    targetAudience: existing.targetAudience !== undefined ? existing.targetAudience : def.targetAudience,
    rawIdea: existing.rawIdea !== undefined ? existing.rawIdea : def.rawIdea,
    refinedIdea: existing.refinedIdea !== undefined ? existing.refinedIdea : def.refinedIdea,
    bigIdeaContent: existing.bigIdeaContent !== undefined ? existing.bigIdeaContent : def.bigIdeaContent,
  };
}

/**
 * Field-level merge for materials with monotonic boolean semantics: once a
 * boolean (deckParsed, siteConfirmed, siteRead) becomes true it MUST stay true — a later
 * model response emitting null (parsed to false by lenientBoolean) must not
 * overwrite a confirmed true. siteRead is only ever written as `true` (absent
 * otherwise) so rows predating the field keep their exact shape. critiqueId uses latest-defined-wins.
 */
function mergeMaterials(
  base: OnboardingResult["materials"],
  next: OnboardingResult["materials"] | undefined,
): OnboardingResult["materials"] {
  if (next === undefined) return base;
  if (next === null) return base;
  if (base === null || base === undefined) return next;
  return {
    deckParsed: base.deckParsed || next.deckParsed,
    siteConfirmed: base.siteConfirmed || next.siteConfirmed,
    ...(base.siteRead || next.siteRead ? { siteRead: true } : {}),
    critiqueId: next.critiqueId !== undefined ? next.critiqueId : base.critiqueId,
  };
}

/**
 * Field-level enrich-only merge for a flat capture group (fundraise, traction,
 * individual step groups). Strings: pick() (non-null wins, never erase prior).
 * Arrays: replace whole when next has a non-null value (items carry no identity
 * to merge on). Null/undefined next preserves base entirely.
 */
function mergeGroup<T extends Record<string, unknown> | null | undefined>(
  base: T,
  next: Partial<NonNullable<T>> | null | undefined,
): T {
  if (next === undefined || next === null) return base;
  if (base === null || base === undefined) return next as T;
  const merged = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(next)) {
    const nv = (next as Record<string, unknown>)[key];
    if (nv === undefined) continue;
    // Arrays: replace whole when next has a NON-EMPTY value. An empty array is
    // treated like null/omission — under delta emission a stray [] from the
    // model must not wipe prior capture (mirrors enrichPickArray).
    if (Array.isArray(nv)) {
      if (nv.length > 0) merged[key] = nv;
    } else if (nv !== null) {
      // Non-null scalar: next wins
      merged[key] = nv;
    }
    // null from next: do NOT overwrite prior (enrich-only)
  }
  return merged as T;
}

/**
 * Merge the `steps` object: field-level inside each group, group-level
 * pick (non-null next group merges INTO base group, never replaces whole).
 */
function mergeSteps(
  base: OnboardingResult["steps"],
  next: PartialSteps | undefined,
): OnboardingResult["steps"] {
  if (next === undefined || next === null) return base;
  if (base === null || base === undefined) return next as OnboardingResult["steps"];
  return {
    customerProblem: mergeGroup(base.customerProblem, next.customerProblem),
    competitive: mergeGroup(base.competitive, next.competitive),
    market: mergeGroup(base.market, next.market),
    brand: mergeGroup(base.brand, next.brand),
    gtm: mergeGroup(base.gtm, next.gtm),
    productBuild: mergeGroup(base.productBuild, next.productBuild),
    team: mergeGroup(base.team, next.team),
  };
}

/**
 * Merges two partial onboarding results, field-by-field, with `next` taking
 * precedence where it has a defined (non-null/undefined) value, and `prior`
 * providing the fallback. Used on the client to accumulate a cumulative
 * snapshot across streaming turns: the LLM is not guaranteed to re-emit every
 * field on every turn, and useObject resets `object` to empty on each new
 * submit, so without this we lose data captured in earlier exchanges if the
 * user skips mid-stream.
 */
export function mergePartialResults(
  prior: PartialOnboardingResult | OnboardingResult | null | undefined,
  next: PartialOnboardingResult | OnboardingResult | null | undefined,
): OnboardingResult {
  const base = mergeWithDefaults(prior);
  if (!next) return base;
  return {
    status: next.status ?? base.status,
    identity: (() => {
      const known = ["linkedinUrl", "name", "email", "title", "background", "personalWebsite"] as const;
      const merged: Record<string, unknown> = {
        ...Object.fromEntries(
          Object.entries(base.identity as Record<string, unknown>).filter(
            ([key]) => !known.includes(key as typeof known[number]),
          ),
        ),
        linkedinUrl: enrichPick(next.identity?.linkedinUrl, base.identity.linkedinUrl),
        name: enrichPick(next.identity?.name, base.identity.name),
        email: enrichPick(next.identity?.email, base.identity.email),
        title: enrichPick(next.identity?.title, base.identity.title),
        background: enrichPick(next.identity?.background, base.identity.background),
        personalWebsite: enrichPick(
          next.identity?.personalWebsite,
          base.identity.personalWebsite,
        ),
      };
      for (const [key, value] of Object.entries(next.identity ?? {})) {
        if (known.includes(key as typeof known[number])) continue;
        if (value !== undefined) merged[key] = value;
      }
      return merged as OnboardingResult["identity"];
    })(),
    journey: {
      stage: next.journey?.stage && next.journey.stage !== "unknown"
        ? next.journey.stage
        : base.journey.stage,
      narrative: enrichPick(next.journey?.narrative, base.journey.narrative),
      milestones: enrichPickArray(next.journey?.milestones, base.journey.milestones),
    },
    bigIdea: {
      oneLiner: enrichPick(next.bigIdea?.oneLiner, base.bigIdea.oneLiner),
      problem: enrichPick(next.bigIdea?.problem, base.bigIdea.problem),
      solution: enrichPick(next.bigIdea?.solution, base.bigIdea.solution),
      targetAudience: enrichPick(next.bigIdea?.targetAudience, base.bigIdea.targetAudience),
      rawIdea: enrichPick(next.bigIdea?.rawIdea, base.bigIdea.rawIdea),
      refinedIdea: enrichPick(next.bigIdea?.refinedIdea, base.bigIdea.refinedIdea),
      bigIdeaContent: enrichPick(next.bigIdea?.bigIdeaContent, base.bigIdea.bigIdeaContent),
    },
    rubricType: next.rubricType != null ? next.rubricType : base.rubricType,
    raisingIntent: next.raisingIntent && next.raisingIntent !== "unknown"
      ? next.raisingIntent
      : base.raisingIntent,
    launchStatus: next.launchStatus && next.launchStatus !== "unknown"
      ? next.launchStatus
      : base.launchStatus,
    brandingStatus: enrichPick(next.brandingStatus, base.brandingStatus),
    materials: mergeMaterials(base.materials, next.materials),
    siteSignals: mergeSiteSignals(base.siteSignals, next.siteSignals),
    notes: next.notes != null ? enrichPickArray(next.notes, base.notes) : base.notes,
    steps: mergeSteps(base.steps, next.steps),
    fundraise: mergeGroup(base.fundraise, next.fundraise),
    traction: mergeGroup(base.traction, next.traction),
  };
}

/**
 * Builds a full OnboardingResult for the Skip flow: merge existing with defaults,
 * then set status to "skipped" and ensure bigIdea.rawIdea is set (from existing or fallback).
 */
export function buildSkipResult(
  existing: PartialOnboardingResult | OnboardingResult | null | undefined,
  rawIdeaFallback: string,
): OnboardingResult {
  const merged = mergeWithDefaults(existing);
  const existingBigIdea = existing && typeof existing === "object" ? (existing as { bigIdea?: { rawIdea?: string | null } }).bigIdea : undefined;
  const rawIdea =
    (existingBigIdea?.rawIdea !== undefined && existingBigIdea.rawIdea !== null
      ? existingBigIdea.rawIdea
      : null) ?? rawIdeaFallback;
  return {
    ...merged,
    status: "skipped",
    bigIdea: { ...merged.bigIdea, rawIdea },
  };
}

/**
 * Returns true if the result has at least one populated bigIdea field.
 * Used to avoid treating status=complete as actionable before bigIdea has streamed.
 * With streamObject, status can arrive before bigIdea; gating prevents "Untitled idea" fallback.
 */
export function hasMinimumBigIdeaData(
  r: PartialOnboardingResult | OnboardingResult | null | undefined,
): boolean {
  if (!r?.bigIdea) return false;
  const b = r.bigIdea;
  return !!(
    (b.refinedIdea && b.refinedIdea.trim()) ||
    (b.oneLiner && b.oneLiner.trim()) ||
    (b.rawIdea && b.rawIdea.trim()) ||
    (b.problem && b.problem.trim())
  );
}

export function hasIdentityResolution(
  r: PartialOnboardingResult | OnboardingResult | null | undefined,
): boolean {
  const i = r?.identity;
  return !!(i?.linkedinUrl?.trim() || i?.personalWebsite?.trim() || i?.background?.trim());
}

/**
 * Returns true only when the fields needed for a trustworthy completed onboarding result are present.
 */
export function hasCompleteBigIdeaData(
  r: PartialOnboardingResult | OnboardingResult | null | undefined,
): boolean {
  const b = r?.bigIdea;
  if (!b) return false;

  return !!(
    b.problem?.trim() &&
    b.solution?.trim() &&
    b.targetAudience?.trim() &&
    b.refinedIdea?.trim()
  );
}

/**
 * Extracts a summary description of what was captured
 * Useful for logging or UI display
 */
export function summarizeOnboardingResult(result: OnboardingResult): string {
  const parts: string[] = [];

  if (result.identity.name) {
    parts.push(`Name: ${result.identity.name}`);
  }

  if (result.journey.stage !== "unknown") {
    parts.push(`Stage: ${result.journey.stage}`);
  }

  if (result.bigIdea.oneLiner) {
    parts.push(`Idea: ${result.bigIdea.oneLiner.substring(0, 100)}...`);
  }

  return parts.length > 0 ? parts.join(" | ") : "No data captured";
}
